import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import FieldHelpMark from '@/components/FieldHelpMark';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import PublishImageUploadZone from '@/tiktok-platform/modules/products/components/PublishImageUploadZone';
import { showToast } from '@/utils/toast';

/** 平台 modelType：0 成衣 / 1 鞋模 */
const MODEL_TYPE_CLOTHES = 0;
const MODEL_TYPE_SHOE = 1;

function pickClothes(initial) {
  const clothes = initial?.clothesModel || initial?.payload?.clothesModel || {};
  return {
    height: String(clothes.height ?? initial?.height ?? initial?.payload?.modelHeight ?? '').trim(),
    bust: String(clothes.bust ?? initial?.bust ?? initial?.payload?.modelBust ?? '').trim(),
    waist: String(clothes.waist ?? initial?.waist ?? initial?.payload?.modelWaist ?? '').trim(),
    hipline: String(
      clothes.hipline ?? initial?.hip ?? initial?.payload?.modelHip ?? '',
    ).trim(),
  };
}

function pickShoe(initial) {
  const shoe = initial?.shoeModel || initial?.payload?.shoeModel || {};
  return {
    footLength: String(
      shoe.footLength ?? initial?.footLength ?? initial?.payload?.modelFootLength ?? '',
    ).trim(),
    footWidth: String(
      shoe.footWidth ?? initial?.footWidth ?? initial?.payload?.modelFootWidth ?? '',
    ).trim(),
  };
}

function resolveModelType(initial) {
  const raw = initial?.modelType ?? initial?.payload?.modelType;
  if (raw === 0 || raw === '0') return MODEL_TYPE_CLOTHES;
  if (raw === 1 || raw === '1') return MODEL_TYPE_SHOE;
  // 发品侧曾用 1=成衣 / 2=鞋模
  if (raw === 2 || raw === '2') return MODEL_TYPE_SHOE;
  return MODEL_TYPE_CLOTHES;
}

function portraitUrlOf(initial) {
  return String(
    initial?.headPortrait
    || initial?.thumbUrl
    || initial?.modelProfileUrl
    || initial?.payload?.headPortrait
    || initial?.payload?.modelProfileUrl
    || '',
  ).trim();
}

/** 已有平台头像 URL → 上传槽位（编辑回显） */
function portraitSlotFromUrl(url) {
  const src = String(url || '').trim();
  if (!src) return null;
  return {
    id: `existing-${src.slice(-24)}`,
    name: i18n.t('modelLib.avatar', { ns: 'workbench' }),
    previewUrl: src,
    publicUrl: src,
    ipeUrl: src,
    tiktokUrl: src,
  };
}

function slotTiktokUrl(slot) {
  if (!slot || typeof slot !== 'object') return '';
  return String(slot.tiktokUrl || slot.url || slot.urls?.[0] || '').trim();
}

/**
 * 模特库 · 新建 / 编辑平台模特（bg.glo.modelinfo.add / edit）。
 */
export default function ModelTemplateCreateModal({
  open,
  shopId,
  initial = null,
  busy = false,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('workbench');
  const isEdit = Boolean(initial?.modelId || initial?.id);
  const [modelName, setModelName] = useState('');
  const [modelType, setModelType] = useState(MODEL_TYPE_CLOTHES);
  const [portraitSlot, setPortraitSlot] = useState(null);
  const [height, setHeight] = useState('');
  const [bust, setBust] = useState('');
  const [waist, setWaist] = useState('');
  const [hipline, setHipline] = useState('');
  const [footLength, setFootLength] = useState('');
  const [footWidth, setFootWidth] = useState('');

  useEffect(() => {
    if (!open) return;
    const clothes = pickClothes(initial);
    const shoe = pickShoe(initial);
    setModelName(String(initial?.name || initial?.modelName || initial?.payload?.modelName || '').trim());
    setModelType(resolveModelType(initial));
    setPortraitSlot(portraitSlotFromUrl(portraitUrlOf(initial)));
    setHeight(clothes.height);
    setBust(clothes.bust);
    setWaist(clothes.waist);
    setHipline(clothes.hipline);
    setFootLength(shoe.footLength);
    setFootWidth(shoe.footWidth);
  }, [open, initial]);

  if (!open) return null;

  const submit = () => {
    const name = String(modelName || '').trim();
    if (!name) {
      showToast(t('modelLib.needName'), 'warning');
      return;
    }
    if (name.length > 64) {
      showToast(t('modelLib.nameTooLong'), 'warning');
      return;
    }
    const type = Number(modelType);
    const body = {
      modelName: name,
      modelType: type,
    };
    const portrait = slotTiktokUrl(portraitSlot);
    if (portrait) body.headPortrait = portrait;

    const isMeasure = (v) => /^\d+(\.\d+)?$/.test(String(v || '').trim());

    if (type === MODEL_TYPE_CLOTHES) {
      const h = String(height || '').trim();
      const b = String(bust || '').trim();
      const w = String(waist || '').trim();
      const hip = String(hipline || '').trim();
      if (!h || !b || !w || !hip) {
        showToast(t('modelLib.clothesMeasures'), 'warning');
        return;
      }
      if (![h, b, w, hip].every(isMeasure)) {
        showToast(t('modelLib.clothesNumber'), 'warning');
        return;
      }
      body.clothesModel = { height: h, bust: b, waist: w, hipline: hip };
    } else {
      const fl = String(footLength || '').trim();
      const fw = String(footWidth || '').trim();
      if (!fl || !fw) {
        showToast(t('modelLib.shoeMeasuresTiktok'), 'warning');
        return;
      }
      if (![fl, fw].every(isMeasure)) {
        showToast(t('modelLib.shoeNumberTiktok'), 'warning');
        return;
      }
      body.shoeModel = { footLength: fl, footWidth: fw };
    }

    if (isEdit) {
      body.id = initial?.modelId || initial?.id;
    }
    onSubmit?.(body);
  };

  return (
    <div className="tko-batch-modal" role="dialog" aria-modal="true" aria-labelledby="tko-model-tpl-title">
      <div className="tko-batch-modal__backdrop" aria-hidden="true" onClick={busy ? undefined : onClose} />
      <div className="tko-batch-modal__panel">
        <div className="tko-batch-modal__head">
          <h2 id="tko-model-tpl-title">{isEdit ? t('modelLib.editModel') : t('modelLib.create')}</h2>
          <button
            type="button"
            className="tko-batch-modal__close"
            disabled={busy}
            onClick={onClose}
            aria-label={t('moduleShell.close')}
          >
            ×
          </button>
        </div>
        <div className="tko-batch-modal__body">
          <div className="tko-model-form">
            <label className="tko-discuss-field">
              <span>
                {t('modelLib.colName')}
                <RequiredMark />
              </span>
              <input
                className="tko-input"
                value={modelName}
                disabled={busy}
                placeholder={t('modelLib.namePh')}
                maxLength={64}
                onChange={(e) => setModelName(e.target.value)}
              />
            </label>

            <div className="tko-discuss-field" role="group" aria-label={t('modelLib.type')}>
              <span>
                {t('modelLib.type')}
                <RequiredMark />
              </span>
              <div className="tko-model-form__type-row">
                <label className="tko-model-form__radio">
                  <input
                    type="radio"
                    name="modelType"
                    checked={modelType === MODEL_TYPE_CLOTHES}
                    disabled={busy || isEdit}
                    onChange={() => setModelType(MODEL_TYPE_CLOTHES)}
                  />
                  {t('modelLib.clothes')}
                </label>
                <label className="tko-model-form__radio">
                  <input
                    type="radio"
                    name="modelType"
                    checked={modelType === MODEL_TYPE_SHOE}
                    disabled={busy || isEdit}
                    onChange={() => setModelType(MODEL_TYPE_SHOE)}
                  />
                  {t('modelLib.shoeTiktok')}
                </label>
              </div>
              {isEdit ? (
                <p className="tko-wizard__inline-hint">{t('modelLib.typeLocked')}</p>
              ) : null}
            </div>

            <div className="tko-discuss-field tko-model-form__portrait">
              <span className="tko-field__label-with-help">
                {t('modelLib.avatar')}
                <FieldHelpMark
                  text={t('modelLib.avatarHintTiktok')}
                />
              </span>
              <PublishImageUploadZone
                compact
                labelInside
                label={t('modelLib.uploadAvatar')}
                imageRule="material"
                imageType={1}
                imageBizType={0}
                multiple={false}
                maxCount={1}
                shopId={shopId}
                value={portraitSlot}
                onChange={(next) => {
                  const item = Array.isArray(next) ? (next[0] || null) : (next || null);
                  setPortraitSlot(item);
                }}
              />
            </div>

            {modelType === MODEL_TYPE_CLOTHES ? (
              <>
                <label className="tko-discuss-field">
                  <span>
                    {t('modelLib.heightLabel')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tko-input"
                    value={height}
                    disabled={busy}
                    placeholder={t('modelLib.phHeight')}
                    onChange={(e) => setHeight(e.target.value)}
                  />
                </label>
                <label className="tko-discuss-field">
                  <span>
                    {t('modelLib.bust')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tko-input"
                    value={bust}
                    disabled={busy}
                    placeholder={t('modelLib.phBust')}
                    onChange={(e) => setBust(e.target.value)}
                  />
                </label>
                <label className="tko-discuss-field">
                  <span>
                    {t('modelLib.waist')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tko-input"
                    value={waist}
                    disabled={busy}
                    placeholder={t('modelLib.phWaist')}
                    onChange={(e) => setWaist(e.target.value)}
                  />
                </label>
                <label className="tko-discuss-field">
                  <span>
                    {t('modelLib.hip')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tko-input"
                    value={hipline}
                    disabled={busy}
                    placeholder={t('modelLib.phHip')}
                    onChange={(e) => setHipline(e.target.value)}
                  />
                </label>
              </>
            ) : (
              <>
                <label className="tko-discuss-field">
                  <span>
                    {t('modelLib.footLength')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tko-input"
                    value={footLength}
                    disabled={busy}
                    placeholder={t('modelLib.phFootLen')}
                    onChange={(e) => setFootLength(e.target.value)}
                  />
                </label>
                <label className="tko-discuss-field">
                  <span>
                    {t('modelLib.footWidth')}
                    <RequiredMark />
                  </span>
                  <input
                    className="tko-input"
                    value={footWidth}
                    disabled={busy}
                    placeholder={t('modelLib.phFootW')}
                    onChange={(e) => setFootWidth(e.target.value)}
                  />
                </label>
              </>
            )}
          </div>
        </div>
        <div className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--secondary" disabled={busy} onClick={onClose}>
            {t('shop.cancel')}
          </button>
          <button type="button" className="tko-btn tko-btn--primary" disabled={busy} onClick={submit}>
            {busy ? t('tiktokPublish.saving') : t('modelLib.savePlatform')}
          </button>
        </div>
      </div>
    </div>
  );
}
